import { h, Component } from 'preact';
import SearchResults from '../collections/search_results.collection';
import { isEmpty } from '../utils/checking.util';
import Location from '../location';
import { directCall } from '../analytics/dtm_helper';
import { PHONE_MEDIA_QUERY } from '../constants/media_query.constants';
import NMRepCard from './nm_rep_card.component';
import FinancialReps from '../collections/financial_reps.collection';
import { newRelicJSError } from '../analytics/new_relic_helper';
import { jsxIf } from '../utils/jsx.util';

export default class NMSearchPage extends Component {
  queryInput;

  constructor(props) {
    super(props);

    this.state = {
      query: Location.queryParams.q,
      didSearch: false,
      isSearching: false,
      results: undefined,
      reps: undefined,
      isOpen: props.isopen || Location.queryParams.searchIsOpen || false,
    };
  }

  componentDidMount () {
    const toggleElements = document.querySelectorAll('[nm-search-toggle]');
    for (let index = 0; index < toggleElements.length; index++) {
      const toggleElm = toggleElements[index];
      toggleElm.addEventListener('click', this.toggle.bind(this));
      toggleElm.addEventListener('keydown', (e) => {
        const key = e.which;
        if (key === 13) { // 13 is enter
          this.toggle();
        }
      });
    }

    // When the user presses escape
    document.addEventListener('keydown', (e) => {
      const key = e.which;
      if (key === 27) { // 27 is escape
        this.close();
      }
    });


    Location.addEventListener('changestate', (event) => {
      if (event.state && event.state.hasOwnProperty('query')) {
        this.setState({ query: event.state.query });
        this.performSearch();
      }
    });

    if (!isEmpty(this.state.query)) {
      this.open();
      this.performSearch();
    }
  }

  get isOpen () {
    return this.state.isOpen;
  }

  setState (values = {}, callback = undefined) {
    for (const key in values) {
      this.state[key] = values[key];
    }
    return super.setState(values, callback);
  }

  open () {
    this.setState({ isOpen: true });
    document.body.style.overflow = 'hidden';
    if (this.queryInput) {
      setTimeout(() => {
        this.queryInput.focus();
      }, 33);
    }
  }

  close () {
    this.setState({ isOpen: false });
    document.body.style.overflow = 'auto';
    if (this.queryInput) {
      this.queryInput.blur();
    }
    Location.updateQueryParams({ q: undefined }, { query: undefined });
  }

  toggle () {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleInputChange (event) {
    const target = event.target || event.srcElement || {};
    const query = target.value || undefined;

    this.setState({ query });
  }

  handleSubmit (event) {
    event.preventDefault();
    event.stopPropagation();

    const { query } = this.state;
    const queryParams = {};

    if (!isEmpty(query)) {
      queryParams.q = encodeURIComponent(query);
    }

    Location.updateQueryParams(queryParams, { query });
  }

  performSearch () {
    // If the query was empty, then don't do anything and clear results
    if (isEmpty(this.state.query)) {
      return this.setState({
        query: undefined,
        results: undefined,
        reps: undefined,
        isSearching: false,
      });
    }

    this.setState({ isSearching: true });

    // Otherwise, perform a search
    Promise.all([
      (new SearchResults())
        .setParam('q', this.state.query)
        .setPageSize(20)
        .promiseFetch(),

      (new FinancialReps())
        .setParam('fullName', this.state.query)
        .promiseFetch(),
    ])
      .then(([results, reps]) => {
        const dataElements = {
          'search-term': this.state.query,
          'search-nub-results': results.resultCount,
        };
        directCall('site-search', dataElements);
        this.setState({ didSearch: true, results, reps });
      })
      .catch((err) => {
        newRelicJSError(err);
        console.error(err);
      }).then(() => {
        this.setState({ isSearching: false });
      });
    return false;
  }

  get shouldShowLoading () {
    return this.state.isSearching;
  }

  get shouldShowSearchResults () {
    if (this.state.isSearching) {
      return false;
    }

    if (!this.state.results) {
      return false;
    }

    return this.state.results.length > 0 && this.state.reps.length !== 1;
  }

  get shouldShowFRResults () {
    if (this.state.isSearching) {
      return false;
    }

    if (!this.state.reps) {
      return false;
    }

    return this.state.reps.length === 1;
  }

  get shouldShowResults () {
    if (this.state.isSearching) {
      return false;
    }

    if (!this.state.didSearch) {
      return false;
    }

    return (this.shouldShowSearchResults || this.shouldShowFRResults);
  }

  get shouldShowNoResults () {
    if (this.state.isSearching) {
      return false;
    }

    if (!this.state.didSearch) {
      return false;
    }

    return !(this.shouldShowSearchResults || this.shouldShowFRResults);
  }

  render () {
    return (
      <div className={[
        'nm-search-page',
        this.isOpen ? 'is-open' : '',
      ].join(' ')}>
        <div className='nm-search-page--background' />
        <div className='nm-search-page--container'>
          <div className='nm-search-page--close-background' onClick={this.close.bind(this)} />
          <a className='nm-search-page--close' onClick={this.close.bind(this)}>
            <picture>
              <source srcset='<%=templateBasePath%>/images/nav/mobile-close.svg' media={PHONE_MEDIA_QUERY} role='button' />
              <img src='<%=templateBasePath%>/images/close.svg' alt='Close' role='button' />
            </picture>
          </a>
          <form className='nm-search-page--form' onSubmit={this.handleSubmit.bind(this)}>
            <span class='is-small' id='searchFor'>Search for specific advisors, products, services, articles, etc.</span>
            <input
              type='search'
              role='searchbox'
              placeholder='What are you looking for?'
              aria-labelledby='searchFor'
              value={this.state.query}
              onChange={this.handleInputChange.bind(this)}
              ref={input => this.queryInput = input}
            />
            <button className='nmx-button nmx-button--small' type='submit' value='Search'>
              Search
            </button>
          </form>
          <div className='nm-search-page--content'>
            {jsxIf(this.shouldShowLoading, () => (
              <div className='loader' />
            ))}
            {jsxIf(this.shouldShowNoResults, () => (
              <span class='is-small'>
                Sorry, there were 0 results matching your search.
              </span>
            ))}
            {jsxIf(this.shouldShowFRResults, () => (
              <div>
                <span class='is-small'>Showing 1 Result for {this.state.query}</span>
                <NMRepCard rep={this.state.reps.first()} campaign='NM.com Search' />
              </div>
            ))}
            {jsxIf(this.shouldShowSearchResults, () => (
              <div>
                {/*
                <span class="is-small">Showing 1-{this.state.results.length} of {this.state.results.resultCount} Results</span>
              */}
                <ul className='nm-search-page--results'>
                  {this.state.results.map(result => (
                    <li>
                      <a href={result.url}>
                        {jsxIf(result.thumbnail, () => (
                          <div class='nm-search-page--result-picture'>
                            <picture>
                              <img src={result.thumbnail} />
                            </picture>
                          </div>
                        ))}
                        <div class='nm-search-page--result-content'>
                          {jsxIf(result.category || result.publishDate, () => (
                            <div class='nm-search-page--result-eyebrow'>
                              {jsxIf(result.category, () => (
                                <strong>{result.category}</strong>
                              ))}
                              {jsxIf(result.publishDate, () => (
                                <span>{result.publishDate}</span>
                              ))}
                            </div>
                          ))}
                          <h3>{result.title}</h3>
                          <p>{result.excerpt}</p>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}
